import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { differenceInCalendarMonths } from 'date-fns';
import { trans, useSoyuzExperiment } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import {
  Modal,
  Button,
  ModalHeader,
  ModalBody,
  ModalFooter,
  HomeSummary,
  PaymentInfo,
  useMediatorModalHandle,
  Heading
} from '../../';

import { ListingCostsShape } from '../../constants/shapes';
import { isMobileScreen } from '../utils/breakpoints';
import {
  formatDisplayDate,
  formatMonthlyPaymentDate,
  getDateAsUTC,
  isDateValid
} from '../utils/dateUtils';

import PaymentTimeline from '../PaymentTimeline';
import PaymentDetails from '../PaymentDetails';

import styles from './PaymentInfoModal.module.scss';

const OPEN_PAYMENT_INFO_MODAL_CHANNEL = 'open_payment_info_modal';

export const PaymentInfoModalButton = ({
  onOpen = () => {},
  dataTest = 'payment-info-open',
  linkText = '',
  linkSize = 'normal',
  linkColor = 'link',
  linkClassName = null,
  linkAlt = false,
  showSummaryInModal = false,
  linkFullWidth = false
}) => {
  const { handleModalOpen } = useMediatorModalHandle({
    channel: OPEN_PAYMENT_INFO_MODAL_CHANNEL
  });

  const handleOpen = () => {
    SoyuzAnalytics.sendGA4Event('summary_payment_open', {
      section: 'payment-sumary'
    });
    handleModalOpen(showSummaryInModal);
    onOpen();
  };

  return (
    <Button
      onClick={handleOpen}
      color={linkColor}
      size={linkSize}
      className={linkClassName}
      alt={linkAlt}
      dataTest={dataTest}
      fullWidth={linkFullWidth}
    >
      {linkText}
    </Button>
  );
};

PaymentInfoModalButton.propTypes = {
  onOpen: PropTypes.func,
  dataTest: PropTypes.string,
  linkText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  linkColor: PropTypes.oneOf([
    'primary',
    'secondary',
    'light',
    'danger',
    'help',
    'complementary',
    'filled',
    'select',
    'green-light',
    'white'
  ]),
  linkSize: PropTypes.oneOf(['normal', 'small', 'large', 'big']),
  linkClassName: PropTypes.string,
  linkAlt: PropTypes.bool,
  showSummaryInModal: PropTypes.bool,
  linkFullWidth: PropTypes.bool
};

export const PaymentInfoModal = ({
  onClose = () => {},
  downPaymentTimeInterval = undefined,
  hasNoDeposit = false,
  areSectionsExpanded = false,
  rentableUnit = {},
  rating = '',
  detailedAdminFee = {},
  showCheckOutDate = true,
  contractDurationType = 'temporal',
  hideDepositElanLaw = false,
  renderLandlordPolicies = null,
  hasRentCollection = false,
  creationDate = null,
  pricing,
  isInstantBooking = false,
  children
}) => {
  const [showSummary, setShowSummary] = useState(false);
  const { isModalOpen, handleModalClose } = useMediatorModalHandle({
    channel: OPEN_PAYMENT_INFO_MODAL_CHANNEL,
    onOpen: showSummaryInModal => {
      setShowSummary(showSummaryInModal);
    }
  });

  const revampListingFeesAndExpenses = useSoyuzExperiment(
    'RevampListingTier01And02V2'
  );

  const isRevampListingFeesAndExpensesExperiment =
    revampListingFeesAndExpenses.get('behaviour') === 'variant';

  const newPaymentTimelineExperiment = useSoyuzExperiment('NewPaymentTimeline');
  const isNewPaymentTimelineExperiment =
    newPaymentTimelineExperiment.get('behaviour') === 'variant';

  let paymentTimelineHeaderSubtitle;
  if (
    isNewPaymentTimelineExperiment &&
    pricing?.checkIn &&
    pricing?.checkOut &&
    isDateValid(pricing.checkIn) &&
    isDateValid(pricing.checkOut)
  ) {
    paymentTimelineHeaderSubtitle = trans('payment_details_modal.subtitle', {
      start: formatMonthlyPaymentDate(pricing.checkIn),
      end: formatMonthlyPaymentDate(pricing.checkOut),
      months: Math.max(
        1,
        differenceInCalendarMonths(
          getDateAsUTC(pricing.checkOut),
          getDateAsUTC(pricing.checkIn)
        )
      )
    });
  }

  const handleClose = () => {
    handleModalClose();
    onClose();
  };

  const currencyIsoCode =
    pricing?.currencyIsoCode || rentableUnit.currencyLabel;

  let paymentSection;
  const defaultTitleKey = isNewPaymentTimelineExperiment
    ? 'payment_details.title'
    : 'payment_details_modal.title';
  if (isNewPaymentTimelineExperiment) {
    paymentSection = (
      <PaymentDetails
        currencyIsoCode={currencyIsoCode}
        listingCosts={pricing?.listingCosts}
        pricing={pricing}
        downPaymentTimeInterval={downPaymentTimeInterval}
        isInstantBooking={isInstantBooking}
        hasNoDeposit={hasNoDeposit}
      />
    );
  } else if (isRevampListingFeesAndExpensesExperiment) {
    paymentSection = (
      <PaymentTimeline
        currencyIsoCode={currencyIsoCode}
        listingCosts={pricing?.listingCosts}
        pricing={pricing}
        downPaymentTimeInterval={downPaymentTimeInterval}
        unCollapseAll
        hasNoDeposit={hasNoDeposit}
        isInstantBooking={isInstantBooking}
      />
    );
  } else {
    paymentSection = (
      <PaymentInfo
        pricing={pricing}
        downPaymentTimeInterval={downPaymentTimeInterval}
        areSectionsExpanded={areSectionsExpanded}
        detailedAdminFee={detailedAdminFee}
        contractDurationType={contractDurationType}
        hideDepositElanLaw={hideDepositElanLaw}
        renderLandlordPolicies={renderLandlordPolicies}
        hasRentCollection={hasRentCollection}
        creationDate={creationDate}
        isInstantBooking={isInstantBooking}
      />
    );
  }

  return (
    <Modal
      ariaHideApp={false}
      contentLabel="modal"
      isOpen={isModalOpen}
      onRequestClose={handleClose}
      size={isMobileScreen() ? 'fullscreen' : 'medium'}
    >
      <ModalHeader
        dataTest="payment-info-close"
        label={
          showSummary
            ? trans('payment_details_modal.title.variant')
            : trans(defaultTitleKey)
        }
        subtitle={paymentTimelineHeaderSubtitle}
        onRequestClose={handleClose}
        className={styles['payment-info-modal__header']}
      />
      <ModalBody
        className={classNames(styles['payment-info-modal__body'], {
          [styles['payment-info-modal__body--new-payment-timeline']]:
            isNewPaymentTimelineExperiment
        })}
      >
        {showSummary && (
          <HomeSummary
            title={rentableUnit.title}
            type={rentableUnit.type}
            photo={rentableUnit.photo || rentableUnit.mainPhotoUrl}
            rating={rating}
            photos={rentableUnit.photos}
          />
        )}
        {showSummary && (
          <div
            className={styles['payment-info-modal__dates']}
            data-test="spotahomeBookingPaymentInfo.dates"
          >
            <div className={styles['payment-info-modal__date-container']}>
              <span className={styles['payment-info-modal__date-title']}>
                {trans('payment_details_modal.move-in').toUpperCase()}
              </span>
              <span
                className={styles['payment-info-modal__date-value']}
                data-test="dates-move-in"
              >
                {formatDisplayDate(pricing.checkIn)}
              </span>
            </div>
            {pricing.checkOut && showCheckOutDate && (
              <div className={styles['payment-info-modal__date-container']}>
                <span className={styles['payment-info-modal__date-title']}>
                  {trans('payment_details_modal.move-out').toUpperCase()}
                </span>
                <span
                  className={styles['payment-info-modal__date-value']}
                  data-test="dates-move-out"
                >
                  {formatDisplayDate(pricing.checkOut)}
                </span>
              </div>
            )}
          </div>
        )}
        {showSummary ? (
          <Heading
            level={3}
            text={trans('payment_details_modal.payment_info')}
            type="capitalize"
            theme="dark"
            align="left"
          />
        ) : null}
        {paymentSection}
      </ModalBody>
      {children ? (
        <ModalFooter className="booking-payment-info-modal__footer">
          {children(handleClose)}
        </ModalFooter>
      ) : null}
    </Modal>
  );
};

PaymentInfoModal.propTypes = {
  onClose: PropTypes.func,
  pricing: PropTypes.shape({
    currencyIsoCode: PropTypes.string.isRequired,
    checkIn: PropTypes.string.isRequired,
    checkOut: PropTypes.string.isRequired,
    adminFee: PropTypes.number,
    downPayment: PropTypes.number.isRequired,
    reservationPrice: PropTypes.number.isRequired,
    tenantPayments: PropTypes.arrayOf(PropTypes.number || PropTypes.string)
      .isRequired,
    securityDeposit: PropTypes.number,
    securityDepositType: PropTypes.string,
    tenantFeeGross: PropTypes.number,
    offer: PropTypes.shape({
      discountType: PropTypes.string,
      discountValue: PropTypes.number
    }),
    listingCosts: ListingCostsShape
  }).isRequired,
  children: PropTypes.func.isRequired,
  downPaymentTimeInterval: PropTypes.shape({
    type: PropTypes.string,
    value: PropTypes.number
  }),
  areSectionsExpanded: PropTypes.bool,
  rentableUnit: PropTypes.shape({
    title: PropTypes.string,
    photo: PropTypes.string,
    type: PropTypes.oneOf(['apartment', 'residence', 'room_shared', 'studio']),
    mainPhotoUrl: PropTypes.string,
    photos: PropTypes.arrayOf(PropTypes.shape({ src: PropTypes.string }))
  }),
  rating: PropTypes.string,
  detailedAdminFee: PropTypes.shape({
    totalToPayOnMoveIn: PropTypes.number
  }),
  showCheckOutDate: PropTypes.bool,
  contractDurationType: PropTypes.string,
  hideDepositElanLaw: PropTypes.bool,
  renderLandlordPolicies: PropTypes.func,
  hasRentCollection: PropTypes.bool,
  creationDate: PropTypes.string,
  hasNoDeposit: PropTypes.bool,
  isInstantBooking: PropTypes.bool
};

export default PaymentInfoModal;
